// src/components/desk/AdminCommentsPanel.jsx — T37: the owner's note to whoever works the flat next.
//
// OWNER 2026-09-03: "only admin" … "comment from admin may be used by you in general to see
// comments and realize what needs to be done." An admin writes "this pin is wrong, it's the
// building behind"; a Claude session reads it (scripts/admin-comments.mjs), does the work, and
// resolves it with what was done. The resolved ones stay on the card as receipts — a resolved
// comment is the reason a change was made.
//
// ⚠️ NOT a channel to an agent. Nobody but admin can read these, so a comment cannot tell the
// person working the flat anything; if it needs to, that is a different surface.
//
// ⚠️ Its own file, and the markup is a separate VIEW taking state as a prop, for the reason
// render-contact-optout.mjs paid for: renderToStaticMarkup does not run effects, so a check that
// mounts the fetching component only ever sees its initial state.
import { useEffect, useState } from 'react'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { btn, input } from './primitives.jsx'
import { getAdminComments, addAdminComment, resolveAdminComment } from '../../db/core.js'
import { fmtTbilisi } from '../../lib/tbilisiTime.js'

export function AdminCommentsPanel({ propertyId, isAdmin = false }) {
  const [comments, setComments] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const [refresh, setRefresh] = useState(0)

  useEffect(() => {
    if (!isAdmin || !propertyId) return
    let gone = false
    getAdminComments(propertyId)
      .then(d => { if (!gone) { setComments(d.comments || []); setErr(null) } })
      .catch(e => { if (!gone) setErr(e.message) })
    return () => { gone = true }
  }, [propertyId, isAdmin, refresh])

  // The gate is the ROLE, not the fetch: a non-admin never mounts the section, so there is no
  // empty panel, no dead control, nothing to explain. The server refuses them anyway.
  if (!isAdmin) return null

  const act = async fn => {
    setBusy(true)
    try { await fn(); setRefresh(r => r + 1); setErr(null) }
    catch (e) { setErr(e.message) }
    finally { setBusy(false) }
  }
  return (
    <AdminCommentsView comments={comments} err={err} busy={busy}
      onAdd={body => act(() => addAdminComment(propertyId, body))}
      onResolve={(id, resolution) => act(() => resolveAdminComment(id, resolution))} />
  )
}

export function AdminCommentsView({ comments, err = null, busy = false, onAdd = () => {}, onResolve = () => {} }) {
  const [draft, setDraft] = useState('')
  const [resolving, setResolving] = useState(null)      // comment id whose resolution is being typed
  const [resolution, setResolution] = useState('')
  const open = (comments || []).filter(c => !c.resolved_at)
  const done = (comments || []).filter(c => c.resolved_at)

  return (
    <section className="rounded-lg border border-border p-3" data-admin-comments>
      <h4 className="text-xs font-medium mb-1">{t('desk_admin_comments_title')}</h4>
      <p className="text-[11px] text-muted-foreground/80 mb-2">{t('desk_admin_comments_hint')}</p>

      {comments === null && !err && <p className="text-xs text-muted-foreground">{t('desk_loading')}</p>}

      {open.map(c => (
        <div key={c.id} className="text-sm py-1.5 border-t border-border/50" data-admin-comment={c.id}>
          <div className="whitespace-pre-wrap">{c.body}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{fmtTbilisi(c.created_at, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
          {resolving === c.id ? (
            <div className="mt-1 flex gap-2">
              <input className={`${input} text-xs`} placeholder={t('desk_admin_comment_resolution_ph')}
                value={resolution} onChange={e => setResolution(e.target.value)} />
              <button type="button" className={btn('primary')} disabled={busy || !resolution.trim()}
                data-resolve-confirm={c.id}
                onClick={() => { onResolve(c.id, resolution.trim()); setResolving(null); setResolution('') }}>
                {t('desk_admin_comment_resolve')}
              </button>
              <button type="button" className={btn('ghost')} onClick={() => { setResolving(null); setResolution('') }}>{t('cancel')}</button>
            </div>
          ) : (
            // Resolving ASKS for a receipt rather than closing on one tap: a resolved comment with
            // no resolution is a change with no stated reason, which is what the table forbids.
            <button type="button" className={`${btn('ghost')} mt-1`} disabled={busy}
              data-resolve={c.id} onClick={() => setResolving(c.id)}>
              {t('desk_admin_comment_mark_resolved')}
            </button>
          )}
        </div>
      ))}

      <div className="mt-2 flex gap-2">
        <input className={`${input} text-sm`} placeholder={t('desk_admin_comment_ph')}
          value={draft} onChange={e => setDraft(e.target.value)} data-admin-comment-draft />
        <button type="button" className={btn('primary')} disabled={busy || !draft.trim()} data-admin-comment-add
          onClick={() => { onAdd(draft.trim()); setDraft('') }}>
          {t('desk_admin_comment_add')}
        </button>
      </div>

      {done.length > 0 && (
        <details className="mt-2">
          <summary className="text-[11px] text-muted-foreground cursor-pointer">
            {tLog('desk_admin_comments_resolved_n', { n: done.length })}
          </summary>
          {done.map(c => (
            <div key={c.id} className="text-xs py-1 border-t border-border/40 text-muted-foreground" data-admin-comment-resolved={c.id}>
              <div className="line-through">{c.body}</div>
              <div className="mt-0.5"><Icon name="check" size={11} /> {c.resolution}</div>
              <div className="text-[10px]">{fmtTbilisi(c.resolved_at, { month: 'short', day: 'numeric' })}</div>
            </div>
          ))}
        </details>
      )}

      {err && <p className="text-xs text-destructive mt-1"><Icon name="alert-triangle" /> {err}</p>}
    </section>
  )
}
